import React from "react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Search, X, Calendar as CalendarIcon, Filter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";
import { Badge } from "@/shared/components/ui/badge";
import { getMerchantName } from "@/shared/utils";

export default function TransactionFilters({
  filters,
  onFilterChange,
  onEntityFilterChange,
  merchants = [],
  resellers = [],
  engines = [],
  providers = [],
  onReset,
  showTypeFilter = true,
}) {
  const { t, language } = useLanguage();

  const locationOptions = [
    { value: "THB", label: "Thai" },
    { value: "IDR", label: "IDR" },
    { value: "PK", label: "Pakistan" },
  ];

  const handleFilterChange = (key, value) => {
    if (
      onEntityFilterChange &&
      (key === "engine" || key === "reseller" || key === "merchant")
    ) {
      onEntityFilterChange(key, value);
    } else {
      onFilterChange({ ...filters, [key]: value });
    }
  };

  const activeFiltersCount = Object.keys(filters).filter((key) => {
    if (key === "search" || key === "dateFrom" || key === "dateTo")
      return !!filters[key];
    return filters[key] !== "all" && filters[key] !== null;
  }).length;

  const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : undefined;
  const dateTo = filters.dateTo ? new Date(filters.dateTo) : undefined;
  console.log("filters: ", filters);

  const selectedDateRangeLabel =
    dateFrom || dateTo
      ? `${dateFrom ? format(dateFrom, "MMM d, yyyy") : "Start"} - ${dateTo ? format(dateTo, "MMM d, yyyy") : "End"}`
      : null;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-6">
      {/* Header with Active Filters */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Filter className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="font-semibold text-slate-800">
            {t("filters") || "Filters"}
          </h2>
          {activeFiltersCount > 0 && (
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none font-medium">
              {activeFiltersCount} {t("activeFilters") || "Active Filters"}
            </Badge>
          )}
        </div>
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-slate-500 hover:text-red-600 hover:bg-red-50 gap-1 transition-colors"
          >
            <X className="h-4 w-4" />
            {t("clearAll") || "Clear All"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("searchIdRefMerchant")}
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="pl-10 h-10 bg-slate-50/50 border-slate-200 focus:bg-white transition-all"
          />
        </div>

        {/* Type Filter */}
        {showTypeFilter && (
          <Select
            value={filters.type || "all"}
            onValueChange={(v) => handleFilterChange("type", v)}
          >
            <SelectTrigger className="h-10 border-slate-200">
              <SelectValue placeholder={t("allTypes") || "All types"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("allTypes") || "All types"}
              </SelectItem>
              <SelectItem value="deposit">{t("deposit")}</SelectItem>
              <SelectItem value="withdrawal">{t("withdrawal")}</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Currency Filter */}
        <Select
          value={filters.currency || "all"}
          onValueChange={(v) => handleFilterChange("currency", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allCurrencies") || "All Currencies"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("allCurrencies") || "All Currencies"}
            </SelectItem>
            <SelectItem value="PKR">Pakistani (Rs PKR)</SelectItem>
            <SelectItem value="THB">Thai (฿ THB)</SelectItem>
            <SelectItem value="IDR">Indonesian (Rp IDR)</SelectItem>
          </SelectContent>
        </Select>

        {/* Location Filter */}
        <Select
          value={filters.location || "all"}
          onValueChange={(v) => handleFilterChange("location", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("location")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allLocations")}</SelectItem>
            {locationOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Engine Filter */}
        <Select
          value={filters.engine || "all"}
          onValueChange={(v) => handleFilterChange("engine", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allEngines") || "All Engines"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("allEngines") || "All Engines"}
            </SelectItem>
            {engines.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Reseller Filter */}
        <Select
          value={filters.reseller || "all"}
          onValueChange={(v) => handleFilterChange("reseller", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allResellers") || "All Resellers"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("allResellers") || "All Resellers"}
            </SelectItem>
            {resellers.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Merchant Filter */}
        <Select
          value={filters.merchant || "all"}
          onValueChange={(v) => handleFilterChange("merchant", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allMerchants") || "All Merchants"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("allMerchants") || "All Merchants"}
            </SelectItem>
            {merchants.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {getMerchantName(m, language, t)} ({m.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Provider Filter */}
        <Select
          value={filters.provider || "all"}
          onValueChange={(v) => handleFilterChange("provider", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allProviders")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allProviders")}</SelectItem>
            {providers.map((p, idx) => {
              const isObj = typeof p === "object" && p !== null;
              const value = isObj
                ? p.value || p.code || p.id || String(idx)
                : p;
              const label = isObj
                ? p.label || p.name || p.nameEn || String(value)
                : String(p);
              const code = isObj ? p.code : null;

              return (
                <SelectItem key={value} value={value}>
                  {label} {code && code !== label ? `(${code})` : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select
          value={filters.status || "all"}
          onValueChange={(v) => handleFilterChange("status", v)}
        >
          <SelectTrigger className="h-10 border-slate-200">
            <SelectValue placeholder={t("allStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="all"
              className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
            >
              {t("allStatus")}
            </SelectItem>
            <SelectItem
              value="pending"
              className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
            >
              {t("pending")}
            </SelectItem>

            <SelectItem
              value="success"
              className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
            >
              {t("success")}
            </SelectItem>

            <SelectItem
              value="failed"
              className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
            >
              {t("declined") || "Declined"}
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Custom Date Range */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-10 justify-start text-left font-normal border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all gap-2"
            >
              <CalendarIcon className="h-4 w-4 text-slate-500" />
              <span
                className={`${selectedDateRangeLabel ? "text-slate-900 font-medium" : "text-slate-700"} truncate`}
              >
                {selectedDateRangeLabel ||
                  t("customDateRange") ||
                  "Custom Date Range"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={{ from: dateFrom, to: dateTo }}
              onSelect={(range) => {
                onFilterChange({
                  ...filters,
                  dateFrom: range?.from ? range.from.toISOString() : undefined,
                  dateTo: range?.to ? range.to.toISOString() : undefined,
                });
              }}
              disabled={{ after: new Date() }}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
